import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { format } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { KorviuNotificationService } from 'core/services/korviu-notification.service';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NetworkError } from 'shared/models/network-error';
import {
  FederalTaxClassification,
  LlcClassification,
  SubmitTaxDocumentPayload,
  TaxDocument
} from 'shared/types/payment-account.interface';
import { TaxDocumentationResidencyStepComponent } from './components/tax-documentation-residency-step.component';
import { TaxDocumentationW9StepComponent } from './components/tax-documentation-w9-step.component';
import { TaxDocumentationW9CertificationStepComponent } from './components/tax-documentation-w9-certification-step.component';
import { TaxDocumentationW8benStepComponent } from './components/tax-documentation-w8ben-step.component';
import { TaxDocumentationW8benCertificationStepComponent } from './components/tax-documentation-w8ben-certification-step.component';
import {
  buildTaxDocumentReadonlyRows,
  buildW8BenPayload,
  buildW9Payload,
  getTaxDocumentFormNameKey,
  getTaxDocumentOriginalFormUrl,
  getTaxDocumentResidencySuffixKey,
  TAX_DOCUMENT_FEDERAL_CLASSIFICATIONS,
  TAX_DOCUMENT_LLC_CLASSIFICATIONS
} from './tax-documentation-contract.helpers';

type TaxDocumentationStep = 'residency' | 'w9' | 'w9-certification' | 'w8ben' | 'w8ben-certification';
type TaxResidency = 'us' | 'non_us';

@Component({
  selector: 'app-tax-documentation-modal',
  imports: [
    NzModalModule,
    TranslatePipe,
    KorviuButtonComponent,
    TaxDocumentationResidencyStepComponent,
    TaxDocumentationW9StepComponent,
    TaxDocumentationW9CertificationStepComponent,
    TaxDocumentationW8benStepComponent,
    TaxDocumentationW8benCertificationStepComponent
  ],
  templateUrl: './tax-documentation-modal.component.html',
  styleUrl: './tax-documentation-modal.component.scss'
})
export class TaxDocumentationModalComponent implements OnInit {
  #translateService = inject(TranslateService);
  #modalRef = inject(NzModalRef);
  #paymentAccountsService = inject(PaymentAccountsService);
  #korviuNotificationService = inject(KorviuNotificationService);
  #destroyRef = inject(DestroyRef);

  isLoading = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  submittedTaxDocument = signal<TaxDocument>(null);
  currentStep = signal<TaxDocumentationStep>('residency');
  selectedResidency = signal<TaxResidency>('us');
  isBusy = computed(() => this.isLoading() || this.isSaving());
  submittedTaxDocumentRows = computed(() => {
    const taxDocument = this.submittedTaxDocument();

    return taxDocument ? buildTaxDocumentReadonlyRows(taxDocument, key => this.#translateService.instant(key)) : [];
  });
  submittedTaxDocumentOriginalFormUrl = computed(() => {
    const taxDocument = this.submittedTaxDocument();

    return taxDocument ? getTaxDocumentOriginalFormUrl(taxDocument.form_type) : '';
  });
  submittedTaxDocumentFormNameKey = computed(() => {
    const taxDocument = this.submittedTaxDocument();

    return taxDocument ? getTaxDocumentFormNameKey(taxDocument.form_type) : '';
  });
  submittedTaxDocumentResidencySuffixKey = computed(() => {
    const taxDocument = this.submittedTaxDocument();

    return taxDocument ? getTaxDocumentResidencySuffixKey(taxDocument.form_type) : '';
  });
  expandedResidencyInfo = signal<Record<TaxResidency, boolean>>({
    us: false,
    non_us: false
  });
  w9DetailsStepControls = [
    'fullLegalName',
    'streetAddress',
    'city',
    'zipCode',
    'state',
    'taxIdNumber',
    'federalTaxClassification'
  ] as const;
  usCountryControl = new FormControl('', { nonNullable: true });

  form = new FormGroup({
    fullLegalName: new FormControl('', { validators: [Validators.required] }),
    streetAddress: new FormControl('', { validators: [Validators.required] }),
    city: new FormControl('', { validators: [Validators.required] }),
    zipCode: new FormControl('', { validators: [Validators.required] }),
    state: new FormControl('', { validators: [Validators.required] }),
    taxIdType: new FormControl<'ssn' | 'ein'>('ssn', { nonNullable: true }),
    taxIdNumber: new FormControl('', { validators: [Validators.required] }),
    federalTaxClassification: new FormControl<FederalTaxClassification | ''>('', { validators: [Validators.required] }),
    llcClassification: new FormControl<LlcClassification | ''>(''),
    iCertify: new FormControl(false, { validators: [Validators.requiredTrue] }),
    signerName: new FormControl('', { validators: [Validators.required] })
  });

  nonUsForm = new FormGroup({
    fullName: new FormControl('', { validators: [Validators.required] }),
    countryOfCitizenship: new FormControl('', { validators: [Validators.required] }),
    permanentResidenceAddress: new FormControl('', { validators: [Validators.required] }),
    permanentCity: new FormControl('', { validators: [Validators.required] }),
    permanentCountry: new FormControl('', { validators: [Validators.required] }),
    sameMailingAddress: new FormControl(true, { nonNullable: true }),
    mailingAddress: new FormControl(''),
    mailingCity: new FormControl(''),
    mailingCountry: new FormControl(''),
    foreignTaxIdNumber: new FormControl('', { validators: [Validators.required, Validators.maxLength(13)] })
  });

  nonUsCertificationForm = new FormGroup({
    iCertify: new FormControl(false, { validators: [Validators.requiredTrue] }),
    signerName: new FormControl('', { validators: [Validators.required] })
  });

  federalTaxClassifications = TAX_DOCUMENT_FEDERAL_CLASSIFICATIONS;
  llcClassifications = TAX_DOCUMENT_LLC_CLASSIFICATIONS;

  ngOnInit(): void {
    this.usCountryControl.setValue(this.#translateService.instant('profile.unitedStates'));
    this._syncLlcClassificationValidation(this.control('federalTaxClassification')?.value === 'llc');
    this.control('federalTaxClassification')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(value => this._syncLlcClassificationValidation(value === 'llc'));
    this._syncNonUsMailingAddressValidation(this.nonUsControl('sameMailingAddress')?.value);
    this.nonUsControl('sameMailingAddress')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(value => this._syncNonUsMailingAddressValidation(!!value));

    this._loadSubmittedTaxDocument();
  }

  control(controlName: string): AbstractControl {
    return this.form.get(controlName);
  }

  nonUsControl(controlName: string): AbstractControl {
    return this.nonUsForm.get(controlName);
  }

  nonUsFormControl(controlName: string): FormControl {
    return this.nonUsForm.get(controlName) as FormControl;
  }

  nonUsCertificationControl(controlName: string): AbstractControl {
    return this.nonUsCertificationForm.get(controlName);
  }

  nonUsCertificationFormControl(controlName: string): FormControl {
    return this.nonUsCertificationForm.get(controlName) as FormControl;
  }

  get w9DetailsFormIsValid(): boolean {
    const baseControlsAreValid = this.w9DetailsStepControls.every(controlName => this.control(controlName)?.valid);

    if (!baseControlsAreValid) {
      return false;
    }

    if (this.control('federalTaxClassification')?.value !== 'llc') {
      return true;
    }

    return !!this.control('llcClassification')?.valid;
  }

  get taxIdTypeControl(): FormControl<'ssn' | 'ein'> {
    return this.control('taxIdType') as FormControl<'ssn' | 'ein'>;
  }

  get taxIdMask(): string {
    return this.taxIdTypeControl.value === 'ein' ? '00-0000000' : '000-00-0000';
  }

  get taxIdLabel(): string {
    return this.#translateService.instant(
      this.taxIdTypeControl.value === 'ein' ? 'profile.employerIdentificationNumber' : 'profile.socialSecurutyNumber'
    );
  }

  get taxIdPlaceholder(): string {
    return this.taxIdTypeControl.value === 'ein' ? 'XX-XXXXXXX' : 'XXX-XX-XXXX';
  }

  get taxIdMaskErrorKey(): string {
    return this.taxIdTypeControl.value === 'ein'
      ? 'profile.invalidEmployerIdentificationNumber'
      : 'profile.invalidSocialSecurityNumber';
  }

  getToday(): string {
    const locale = this.#translateService.currentLang === 'es' ? es : enUS;
    return format(new Date(), 'MMMM d, yyyy', { locale });
  }

  selectResidency(residency: TaxResidency): void {
    this.selectedResidency.set(residency);
  }

  toggleResidencyInfo(residency: TaxResidency, event?: MouseEvent): void {
    event?.stopPropagation();
    this.expandedResidencyInfo.update(current => ({
      ...current,
      [residency]: !current[residency]
    }));
  }

  isResidencyInfoExpanded(residency: TaxResidency): boolean {
    return this.expandedResidencyInfo()[residency];
  }

  goToSelectedResidencyStep(): void {
    this.currentStep.set(this.selectedResidency() === 'us' ? 'w9' : 'w8ben');
  }

  backToResidencySelection(): void {
    this.currentStep.set('residency');
  }

  backToW9ResidencyStep(): void {
    this.currentStep.set('residency');
  }

  goToW9CertificationStep(): void {
    this.w9DetailsStepControls.forEach(controlName => this.control(controlName)?.markAsTouched());

    if (!this.w9DetailsFormIsValid) {
      return;
    }

    this.currentStep.set('w9-certification');
  }

  backToW9DetailsStep(): void {
    this.currentStep.set('w9');
  }

  continueNonUsFlow(): void {
    if (this.nonUsForm.invalid) {
      this.nonUsForm.markAllAsTouched();
      return;
    }

    this.currentStep.set('w8ben-certification');
  }

  saveNonUsDocumentation(): void {
    if (this.nonUsCertificationForm.invalid) {
      this.nonUsCertificationForm.markAllAsTouched();
      return;
    }

    const body = this._buildW8BenPayload();

    this.isSaving.set(true);
    this.#paymentAccountsService
      .submitTaxDocument(body)
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: () => {
          this.#korviuNotificationService.showSuccess(
            this.#translateService.instant('profile.taxDocumentationSuccessfullySubmitted')
          );
          this.#modalRef.destroy(true);
        },
        error: () => this._showSubmitError()
      });
  }

  closeModal(): void {
    this.#modalRef.destroy();
  }

  saveTaxDocumentation(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const body = this._buildW9Payload();

    this.isSaving.set(true);
    this.#paymentAccountsService
      .submitTaxDocument(body)
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: () => {
          this.#korviuNotificationService.showSuccess(
            this.#translateService.instant('profile.taxDocumentationSuccessfullySubmitted')
          );
          this.#modalRef.destroy(true);
        },
        error: () => this._showSubmitError()
      });
  }

  private _loadSubmittedTaxDocument(): void {
    this.isLoading.set(true);
    this.#paymentAccountsService
      .getMyTaxDocument()
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: response => {
          const taxDocument = this._extractPayload<TaxDocument>(response);
          this.submittedTaxDocument.set(taxDocument);
          this.currentStep.set('residency');
        },
        error: (error: NetworkError) => {
          if (error?.code === 404) {
            return;
          }
        }
      });
  }

  private _extractPayload<T>(response: any): T {
    return (response?.data ?? response) as T;
  }

  private _showSubmitError(): void {
    this.#korviuNotificationService.showError(this.#translateService.instant('profile.taxDocumentationGenericError'));
  }

  private _buildW9Payload(): SubmitTaxDocumentPayload {
    return buildW9Payload({
      fullLegalName: this.form.value.fullLegalName ?? '',
      streetAddress: this.form.value.streetAddress ?? '',
      city: this.form.value.city ?? '',
      state: this.form.value.state ?? '',
      zipCode: this.form.value.zipCode ?? '',
      country: this.#translateService.instant('profile.unitedStates'),
      taxIdType: this.form.value.taxIdType ?? 'ssn',
      taxIdNumber: this.form.value.taxIdNumber ?? '',
      federalTaxClassification: this.form.value.federalTaxClassification as FederalTaxClassification,
      llcClassification: this.form.value.llcClassification,
      iCertify: !!this.form.value.iCertify,
      signerName: this.form.value.signerName ?? '',
      signedAt: new Date().toISOString()
    });
  }

  private _buildW8BenPayload(): SubmitTaxDocumentPayload {
    return buildW8BenPayload({
      fullName: this.nonUsForm.value.fullName ?? '',
      countryOfCitizenship: this.nonUsForm.value.countryOfCitizenship ?? '',
      permanentResidenceAddress: this.nonUsForm.value.permanentResidenceAddress ?? '',
      permanentCity: this.nonUsForm.value.permanentCity ?? '',
      permanentCountry: this.nonUsForm.value.permanentCountry ?? '',
      sameMailingAddress: !!this.nonUsForm.value.sameMailingAddress,
      mailingAddress: this.nonUsForm.value.mailingAddress,
      mailingCity: this.nonUsForm.value.mailingCity,
      mailingCountry: this.nonUsForm.value.mailingCountry,
      foreignTaxIdNumber: this.nonUsForm.value.foreignTaxIdNumber ?? '',
      iCertify: !!this.nonUsCertificationForm.value.iCertify,
      signerName: this.nonUsCertificationForm.value.signerName ?? '',
      signedAt: new Date().toISOString()
    });
  }

  private _syncLlcClassificationValidation(isLlcSelected: boolean): void {
    const control = this.control('llcClassification') as FormControl;

    if (!control) {
      return;
    }

    if (isLlcSelected) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
      control.setValue('');
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private _syncNonUsMailingAddressValidation(sameMailingAddress: boolean): void {
    const mailingControls = ['mailingAddress', 'mailingCity', 'mailingCountry'];

    mailingControls.forEach(controlName => {
      const control = this.nonUsControl(controlName) as FormControl;

      if (!control) {
        return;
      }

      if (sameMailingAddress) {
        control.clearValidators();
        control.setValue('');
      } else {
        control.setValidators([Validators.required]);
      }

      control.updateValueAndValidity({ emitEvent: false });
    });
  }
}
